import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, Upload, LayoutDashboard, Boxes, FileText, ShieldCheck, Mail, Search, ChevronRight, LifeBuoy, Database } from 'lucide-react';
import { DarkBackground } from '@/components/DarkBackground';
import { Input } from '@/components/ui/input';
import { buttonClassName } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { labelClassName } from '@/components/ui/label';
import { marked } from 'marked';
import { sanitizeHtml } from '@/lib/sanitizeHtml';
import { useEntitledToolkits } from '@/hooks/useEntitledToolkits';
import { useT } from '@/lib/i18n';
import { AUDIT_RETENTION_DAYS } from '@/lib/utils';
import type { messages as MSG } from '@/lib/i18n.messages';
import type { ToolkitId } from '@/types/models';

type TKey = keyof typeof MSG;
type Section = {
  id: string;
  titleKey: TKey;
  bodyKey: TKey;
  icon: React.ReactNode;
  /** When set, section is shown only if the org is entitled to this toolkit. */
  requiresToolkit?: ToolkitId;
};

const SECTIONS: Section[] = [
  { id: 'start',         titleKey: 'help.s.start.title',         bodyKey: 'help.s.start.body',         icon: <Sparkles className="h-4 w-4" /> },
  { id: 'toolkits',      titleKey: 'help.s.toolkits.title',      bodyKey: 'help.s.toolkits.body',      icon: <Boxes className="h-4 w-4" /> },
  { id: 'toolkit-audit', titleKey: 'help.s.toolkit_audit.title', bodyKey: 'help.s.toolkit_audit.body', icon: <Upload className="h-4 w-4" />, requiresToolkit: 'audit' },
  { id: 'dashboard',     titleKey: 'help.s.dashboard.title',     bodyKey: 'help.s.dashboard.body',     icon: <LayoutDashboard className="h-4 w-4" /> },
  { id: 'documents',     titleKey: 'help.s.documents.title',     bodyKey: 'help.s.documents.body',     icon: <FileText className="h-4 w-4" /> },
  { id: 'support',       titleKey: 'help.s.support.title',       bodyKey: 'help.s.support.body',       icon: <LifeBuoy className="h-4 w-4" /> },
  { id: 'security',      titleKey: 'help.s.security.title',      bodyKey: 'help.s.security.body',      icon: <ShieldCheck className="h-4 w-4" /> },
  { id: 'data-collected', titleKey: 'help.s.data.title',         bodyKey: 'help.s.data.body',          icon: <Database className="h-4 w-4" /> },
];

/** Variables injectées dans les corps d'aide (rétention affichée = AUDIT_RETENTION_DAYS). */
const BODY_VARS = { n: AUDIT_RETENTION_DAYS };

marked.use({ gfm: true, breaks: true });

const helpRenderer = new marked.Renderer();
const defaultLink = helpRenderer.link.bind(helpRenderer);
helpRenderer.link = function link(token) {
  const { href, title, tokens } = token;
  const text = this.parser.parseInline(tokens);
  if (href?.startsWith('/')) {
    const titleAttr = title ? ` title="${title}"` : '';
    return `<a href="${href}"${titleAttr} data-internal-link="true" class="font-medium text-brand-600 underline decoration-brand-300 underline-offset-2 hover:text-brand-700 dark:text-brand-300 dark:decoration-brand-700 dark:hover:text-brand-200">${text}</a>`;
  }
  return defaultLink(token);
};

function renderHelpMarkdown(source: string): string {
  return sanitizeHtml(marked.parse(source, { renderer: helpRenderer }) as string);
}

export default function Help() {
  const { t } = useT();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const entitledToolkits = useEntitledToolkits();
  const [search, setSearch] = useState('');

  const entitledIds = useMemo(
    () => new Set(entitledToolkits.map(tk => tk.id)),
    [entitledToolkits],
  );

  const visibleSections = useMemo(
    () => SECTIONS.filter(sec => !sec.requiresToolkit || entitledIds.has(sec.requiresToolkit)),
    [entitledIds],
  );

  const onBodyClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[data-internal-link]');
    if (!anchor?.pathname) return;
    e.preventDefault();
    navigate(`${anchor.pathname}${anchor.search}${anchor.hash}`);
  }, [navigate]);

  const filtered = useMemo(() => {
    if (!search.trim()) return visibleSections;
    const s = search.toLowerCase();
    return visibleSections.filter(sec =>
      t(sec.titleKey).toLowerCase().includes(s) || t(sec.bodyKey, BODY_VARS).toLowerCase().includes(s),
    );
  }, [search, t, visibleSections]);

  // Lien profond (/help#data-collected) : la section est rendue après le premier paint.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash, filtered]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-ink-900 dark:text-white">{t('help.title')}</h1>
          <p className="mt-1 text-sm text-ink-500">{t('help.subtitle')}</p>
        </div>
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-400" />
          <Input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('help.search.ph')}
            leadingIcon
          />
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
        {/* Table of contents */}
        <aside className="hidden lg:block">
          <div className="sticky top-20 rounded-xl border border-slate-200 bg-white p-3 shadow-soft dark:border-ink-700 dark:bg-ink-800">
            <div className={labelClassName('mb-2 px-2')}>{t('help.toc')}</div>
            <ul className="space-y-0.5 text-sm">
              {visibleSections.map(s => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="flex items-center gap-2 rounded-md px-2 py-1.5 text-ink-700 hover:bg-slate-50 hover:text-brand-600 dark:text-ink-200 dark:hover:bg-ink-700 dark:hover:text-brand-300">
                    {s.icon}
                    <span className="flex-1 truncate">{t(s.titleKey)}</span>
                    <ChevronRight className="h-3 w-3 text-ink-400" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Body */}
        <main className="space-y-6">
          {filtered.length === 0 && (
            <Card className="text-sm text-ink-500">{t('empty.no_results')}</Card>
          )}
          {filtered.map(s => (
            <Card id={s.id} key={s.id} className="scroll-mt-24">
              <div className="mb-3 flex items-center gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300">
                  {s.icon}
                </div>
                <h2 className="font-display text-lg text-ink-900 dark:text-white">{t(s.titleKey)}</h2>
              </div>
              <div
                className="markdown-body text-sm text-ink-700 dark:text-ink-200"
                onClick={onBodyClick}
                dangerouslySetInnerHTML={{ __html: renderHelpMarkdown(t(s.bodyKey, BODY_VARS)) }}
              />
            </Card>
          ))}

          {/* Support ticket CTA */}
          <Card className="relative isolate flex flex-col items-start gap-3 overflow-hidden sm:flex-row sm:items-center sm:justify-between">
            <DarkBackground variant="section" showGrid={false} />
            <div className="relative z-10 flex items-start gap-3">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
                <LifeBuoy className="h-4 w-4" />
              </div>
              <div>
                <div className="font-medium text-ink-900 dark:text-white">{t('help.contact.issue.title')}</div>
                <div className="text-xs text-ink-500">{t('support.help.cta.desc')}</div>
              </div>
            </div>
            <button
              onClick={() => navigate('/support')}
              className={buttonClassName('primary', 'relative z-10')}
            >
              <LifeBuoy className="h-4 w-4" /> {t('support.help.cta')}
            </button>
          </Card>

          {/* Email fallback */}
          <Card className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-slate-100 text-slate-500 dark:bg-ink-700 dark:text-ink-300">
                <Mail className="h-4 w-4" />
              </div>
              <div>
                <div className="font-medium text-ink-900 dark:text-white">{t('help.contact.question.title')}</div>
                <div className="text-xs text-ink-500">{t('help.contact.desc')}</div>
              </div>
            </div>
            <a
              href="mailto:support@cstools.ai?subject=Aide%20portail%20AUDIT%20AI"
              className={buttonClassName('secondary')}
            >
              <Mail className="h-4 w-4" /> {t('help.contact.cta')}
            </a>
          </Card>
        </main>
      </div>
    </div>
  );
}
